import React from 'react';
import { ArrowDown, Heart, Music } from 'lucide-react';
import { Button } from './ui/button';

const Hero = () => {
  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      {/* Background with gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#1a1a1a] to-[#0a0a0a]" />
      
      {/* Animated background elements */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#D4AF37] rounded-full filter blur-[120px] animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#E63946] rounded-full filter blur-[120px] animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        {/* Badge */}
        <div className="inline-flex items-center space-x-2 bg-[#1a1a1a] border border-[#D4AF37]/30 rounded-full px-6 py-2 mb-8 animate-fade-in">
          <Music className="w-4 h-4 text-[#D4AF37]" />
          <span className="text-[#D4AF37] font-semibold text-sm">Gospel & Hip-Hop Rehabilitation</span>
        </div>

        {/* Main Headline */}
        <h1 className="text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-bold mb-6 leading-none animate-fade-in-up" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>
          <span className="text-white">SPONSOR A</span>
          <br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] via-[#FFD700] to-[#D4AF37] animate-gradient">
            TRANSFORMATION
          </span>
        </h1>

        {/* Subheadline - California Crisis */}
        <p className="text-xl sm:text-2xl text-gray-300 max-w-3xl mx-auto mb-4 leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
          California is defunding rehabilitation programs.
        </p>
        <p className="text-lg sm:text-xl text-gray-400 max-w-3xl mx-auto mb-12 leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
          Your $60 fills the gap with 3 life-changing workbooks and the Urbanation album, empowering those left behind to transform through music, faith, and self-discovery.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
          <Button
            onClick={() => window.open('https://thegardensofhope.org/donate-to-the-gardens-of-hope-donorbox/', '_blank')}
            className="w-full sm:w-auto max-w-full bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0a0a0a] hover:from-[#FFD700] hover:to-[#D4AF37] font-bold px-6 sm:px-10 py-6 sm:py-7 text-base sm:text-xl whitespace-normal leading-tight break-words transition-all duration-300 transform hover:scale-105 shadow-lg shadow-[#D4AF37]/30"
          >
            <Heart className="w-5 h-5 mr-2 flex-shrink-0" />
            SPONSOR NOW - $60
          </Button>
          <Button
            onClick={() => {
              const element = document.getElementById('program');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}
            variant="outline"
            className="w-full sm:w-auto max-w-full border-2 border-[#E63946] text-[#E63946] hover:bg-[#E63946] hover:text-white font-bold px-6 sm:px-10 py-6 sm:py-7 text-base sm:text-xl whitespace-normal leading-tight break-words transition-all duration-300"
          >
            LEARN MORE
          </Button>
        </div>

        {/* Donation Instructions */}
        <div className="max-w-3xl mx-auto mb-8 bg-[#1a1a1a] border border-[#D4AF37]/30 rounded-xl p-6 animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
          <p className="text-gray-300 text-center leading-relaxed">
            We are grateful for our partners at <span className="text-[#D4AF37] font-bold">Gardens of Hope (501c)(3)</span>, who receive donations on behalf of our Sponsor a Transformation initiative. 
            <span className="block mt-2 text-sm text-gray-400">
              Select "Sponsor a Transformation" under the program dropdown and add recipient's name and address in the comments.
            </span>
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-4xl mx-auto animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
          <div className="bg-[#1a1a1a] border border-[#D4AF37]/20 rounded-lg p-6 transform hover:scale-105 transition-transform duration-300">
            <div className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-[#FFD700] mb-2" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>
              3
            </div>
            <div className="text-gray-300 font-medium">Workbooks Included</div>
          </div>
          <div className="bg-[#1a1a1a] border border-[#D4AF37]/20 rounded-lg p-6 transform hover:scale-105 transition-transform duration-300">
            <div className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-[#FFD700] mb-2" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>
              1
            </div>
            <div className="text-gray-300 font-medium">Free Album: Urbanation</div>
          </div>
          <div className="bg-[#1a1a1a] border border-[#D4AF37]/20 rounded-lg p-6 transform hover:scale-105 transition-transform duration-300">
            <div className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-[#FFD700] mb-2" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>
              $60
            </div>
            <div className="text-gray-300 font-medium">Complete Package</div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ArrowDown className="w-8 h-8 text-[#D4AF37]" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
